import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useRessource } from '../hooks/useRessource';
import { ErreurApi, api } from '../lib/apiClient';
import {
  Badge,
  Chargement,
  EnTetePage,
  Erreur,
  Infobulle,
  Tableau,
  type Colonne,
} from '../components/Elements';
import { BandeauAnneeFigee } from '../components/SelecteurAnnee';
import { useAnnee } from '../hooks/useAnnee';
import { FormulaireCreneau, FormulaireDuplication } from './FormulaireCreneau';
import { CommunicationGroupee } from '../components/CommunicationGroupee';
import {
  formaterDuree,
  formaterHeure,
  formaterMontant,
  formaterPlage,
  libelleJour,
} from '../lib/format';
import type { Activite, Creneau, Lieu, Personnel } from '../types';

/**
 * Gestion des créneaux — la grille des cours de l'année.
 *
 * C'est une opération de début d'année : on en crée beaucoup d'un coup,
 * puis on ajuste à la marge. Une liste s'y prête mieux que la grille du
 * planning, qui reste la vue de lecture.
 */
export function CreneauxPage() {
  const { peut, entiteActive } = useAuth();
  const [filtres, setFiltres] = useState({
    activiteId: '',
    personnelId: '',
    lieuId: '',
    jour: '',
    // Les creneaux fermes ne se montrent que sur demande : ils ne sont
    // nulle part ailleurs, et c'est ici qu'on les rouvre.
    inclureInactifs: false,
  });
  const [edition, setEdition] = useState<{ ouverte: boolean; creneau: Creneau | null }>({
    ouverte: false,
    creneau: null,
  });
  // « Ecrire aux eleves » d'un creneau, depuis le menu de sa fiche (2026-10-08).
  const [ecrire, setEcrire] = useState<Creneau | null>(null);
  const [duplication, setDuplication] = useState(false);

  const cle = entiteActive?.id ?? '';
  const annee = useAnnee();
  // Le referentiel est annuel : les activites proposees sont celles de
  // l'annee affichee, pas un cumul de toutes les annees.
  const activites = useRessource(
    () =>
      annee.anneeId
        ? api.get<Activite[]>('/api/activites', { anneeId: annee.anneeId })
        : Promise.resolve([]),
    [cle, annee.anneeId],
  );
  const personnel = useRessource(() => api.get<Personnel[]>('/api/personnel'), [cle]);
  const lieux = useRessource(() => api.get<Lieu[]>('/api/lieux'), [cle]);

  // TOUTE la grille de l'annee, filtree ici et non par le serveur
  // (2026-09-29) : c'est ce qui permet aux filtres de se repondre — les
  // encadrants proposes sont ceux qui ont un creneau au lieu choisi, les
  // lieux ceux ou l'encadrant choisi donne cours, et rien n'est propose
  // qui n'existe pas sur l'annee affichee. Deux cents lignes tiennent en
  // une charge.
  const creneaux = useRessource(
    () =>
      annee.anneeId
        ? api.get<Creneau[]>('/api/creneaux', {
            anneeId: annee.anneeId,
            inclureInactifs: filtres.inclureInactifs || undefined,
          })
        : Promise.resolve([]),
    [cle, annee.anneeId, filtres.inclureInactifs],
  );

  type CleFiltre = 'activiteId' | 'personnelId' | 'lieuId' | 'jour';
  const CLES: CleFiltre[] = ['activiteId', 'personnelId', 'lieuId', 'jour'];

  // Un creneau passe les filtres — tous, ou tous SAUF un : c'est avec ce
  // « sauf » que chaque liste deroulante se calcule depuis les trois
  // autres, sans se restreindre elle-meme.
  const passe = (c: Creneau, f: typeof filtres, sauf: CleFiltre | null = null) =>
    (sauf === 'activiteId' || !f.activiteId || c.activite_id === f.activiteId) &&
    (sauf === 'personnelId' ||
      !f.personnelId ||
      c.encadrants.some((e) => e.personnel_id === f.personnelId)) &&
    (sauf === 'lieuId' || !f.lieuId || c.lieu_id === f.lieuId) &&
    (sauf === 'jour' || !f.jour || String(c.jour) === f.jour);

  const tous = creneaux.donnees ?? [];

  // Poser un filtre, puis effacer ceux que ce choix rend vains : un
  // encadrant qui n'a aucun creneau au lieu qu'on vient de choisir n'est
  // plus une selection, c'est une impasse.
  const poserFiltre = (cle: CleFiltre, valeur: string) => {
    const suite = { ...filtres, [cle]: valeur };
    for (const autre of CLES) {
      if (autre === cle || !suite[autre]) continue;
      if (!tous.some((c) => passe(c, suite))) suite[autre] = '';
    }
    setFiltres(suite);
  };

  const options = (sauf: CleFiltre) => tous.filter((c) => passe(c, filtres, sauf));
  const activitesProposees = [
    ...new Map(options('activiteId').map((c) => [c.activite_id, c.activite_libelle])),
  ].sort((a, b) => a[1].localeCompare(b[1]));
  const encadrantsProposes = [
    ...new Map(
      options('personnelId').flatMap((c) =>
        c.encadrants.map((e) => [e.personnel_id, e] as const),
      ),
    ).values(),
  ].sort((a, b) => a.prenom_usuel.localeCompare(b.prenom_usuel));
  // Deux professeurs s'appellent Jeremy : le nom lève l'homonymie, mais
  // seulement quand elle se presente.
  const libelleEncadrant = (e: (typeof encadrantsProposes)[number]) =>
    encadrantsProposes.filter((x) => x.prenom_usuel === e.prenom_usuel).length > 1
      ? `${e.prenom_usuel} ${e.nom ?? ''}`.trim()
      : e.prenom_usuel;
  const lieuxProposes = [
    ...new Map(
      options('lieuId')
        .filter((c) => c.lieu_id && c.lieu_code)
        .map((c) => [c.lieu_id!, c.lieu_code!]),
    ),
  ].sort((a, b) => a[1].localeCompare(b[1]));
  const joursProposes = [...new Set(options('jour').map((c) => c.jour))].sort((a, b) => a - b);

  // Une annee cloturee est figee (RG-27) : on n'y propose aucun geste
  // d'ecriture, le serveur les refuserait de toute facon.
  const peutEcrire = annee.modifiable;

  const supprimer = async (creneau: Creneau) => {
    if (
      !window.confirm(
        `Supprimer le créneau ${creneau.activite_libelle} du ${libelleJour(creneau.jour).toLowerCase()} ${formaterPlage(creneau.heure_debut, creneau.heure_fin)} ?`,
      )
    ) {
      return;
    }
    try {
      await api.delete(`/api/creneaux/${creneau.id}`);
      creneaux.recharger();
    } catch (cause) {
      window.alert(cause instanceof ErreurApi ? cause.message : 'Suppression impossible.');
    }
  };

  // Fermer ou rouvrir, depuis le menu ⋮ de la fiche. La regle (pas
  // d'inscrit actif) est tenue par le serveur : on affiche son message.
  const changerStatut = async (creneau: Creneau, actif: boolean) => {
    try {
      await api.patch(`/api/creneaux/${creneau.id}/statut`, { actif });
      creneaux.recharger();
    } catch (cause) {
      window.alert(cause instanceof ErreurApi ? cause.message : 'Changement impossible.');
    }
  };

  if (annee.chargement) return <Chargement />;

  if (!annee.annee) {
    return (
      <>
        <EnTetePage titre="Créneaux des ateliers" />
        <div className="carte p-6 text-sm text-encre-500">
          Aucune année scolaire sur cette entité. Créez-en une depuis{' '}
          <Link to="/annees" className="underline">
            Années scolaires
          </Link>
          .
        </div>
      </>
    );
  }

  const liste = tous.filter((c) => passe(c, filtres));

  const colonnes: Colonne<Creneau>[] = [
    {
      cle: 'quand',
      entete: 'Quand',
      // Une seule ligne : c'est ainsi qu'on saisit un creneau — jour, heure
      // de debut, duree. L'heure de fin en decoule et n'a pas a etre lue.
      rendu: (c) => (
        <span className="whitespace-nowrap">
          <span className="font-medium">{libelleJour(c.jour)}</span>
          <span className="text-encre-400"> · </span>
          <span className="tabulaire">{formaterHeure(c.heure_debut)}</span>{' '}
          <span className="text-xs text-encre-500">({formaterDuree(c.duree_minutes)})</span>
          {!c.actif && (
            <span className="ml-2">
              <Badge ton="alerte">inactif</Badge>
            </span>
          )}
        </span>
      ),
    },
    {
      cle: 'activite',
      entete: 'Activité',
      rendu: (c) => (
        <span className="font-medium">
          {c.activite_libelle}
          <span className="ml-2 text-xs font-normal text-encre-400">{c.activite_type}</span>
        </span>
      ),
    },
    {
      cle: 'encadrement',
      entete: 'Encadrement',
      rendu: (c) =>
        c.encadrants.length > 0 ? (
          <span className="flex flex-wrap items-baseline gap-x-1.5">
            {/* Le titulaire d'abord, les autres en plus discret : ce sont
                des co-encadrants, pas des cours supplementaires. */}
            <span>{c.encadrants[0]!.prenom_usuel}</span>
            {c.encadrants.slice(1).map((e) => (
              <span key={e.personnel_id} className="text-xs text-encre-500">
                + {e.prenom_usuel}
              </span>
            ))}
          </span>
        ) : (
          <Badge ton="alerte">Non attribué</Badge>
        ),
    },
    {
      cle: 'lieu',
      entete: 'Lieu',
      rendu: (c) => (c.lieu_code ? <Badge>{c.lieu_code}</Badge> : <Badge ton="alerte">—</Badge>),
    },
    {
      cle: 'effectif',
      entete: 'Inscrits',
      classe: 'text-right',
      rendu: (c) => (
        <span className="tabulaire">
          {c.effectif}
          {c.capacite_max !== null && (
            <span className={c.effectif > c.capacite_max ? 'text-amber-700' : 'text-encre-400'}>
              /{c.capacite_max}
            </span>
          )}
          {c.effectif_historique > 0 && (
            <Infobulle texte="Inscriptions inactives" className="ml-1 text-xs text-encre-400">
              +{c.effectif_historique}
            </Infobulle>
          )}
        </span>
      ),
    },
    {
      cle: 'ca',
      entete: 'Cotisations',
      classe: 'text-right',
      rendu: (c) => <span className="tabulaire">{formaterMontant(c.ca_centimes)}</span>,
    },
    {
      cle: 'actions',
      entete: '',
      classe: 'text-right',
      rendu: (c) => (
        <div className="flex justify-end gap-3">
          {peut('creneau.supprimer') && peutEcrire && c.effectif + c.effectif_historique === 0 && (
            <button
              type="button"
              className="text-sm text-red-600 underline"
              onClick={(evenement) => {
                evenement.stopPropagation();
                void supprimer(c);
              }}
            >
              Supprimer
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <EnTetePage
        titre="Créneaux des ateliers"
        description="La grille des cours se pose en début d’année, puis s’ajuste à la marge. Chaque créneau lie une activité, un jour, une heure de début et une durée — la fin en découle."
        actions={
          <>
            {peut('creneau.creer') && peutEcrire && (
              <>
                <button
                  type="button"
                  className="bouton-secondaire"
                  onClick={() => setDuplication(true)}
                >
                  Reprendre une année
                </button>
                <button
                  type="button"
                  className="bouton-principal"
                  onClick={() => setEdition({ ouverte: true, creneau: null })}
                >
                  Nouveau créneau
                </button>
              </>
            )}
          </>
        }
      />

      {!peutEcrire && <BandeauAnneeFigee annee={annee.annee} />}

      <form className="carte mb-4 flex flex-wrap items-end gap-3 p-4">
        {/* Quatre listes de MEME largeur (`w-44`, comme les ecrans de
            liste), dont les options se deduisent les unes des autres : on
            ne propose que ce qui a un creneau sur l'annee affichee, et
            qui reste compatible avec les autres filtres. */}
        <label>
          <span className="etiquette">Activité</span>
          <select
            className="champ w-44"
            value={filtres.activiteId}
            onChange={(e) => poserFiltre('activiteId', e.target.value)}
          >
            <option value="">Toutes</option>
            {activitesProposees.map(([id, libelle]) => (
              <option key={id} value={id}>
                {libelle}
              </option>
            ))}
          </select>
        </label>

        <label>
          <span className="etiquette">Encadrant</span>
          <select
            className="champ w-44"
            value={filtres.personnelId}
            onChange={(e) => poserFiltre('personnelId', e.target.value)}
          >
            <option value="">Tous</option>
            {encadrantsProposes.map((e) => (
              <option key={e.personnel_id} value={e.personnel_id}>
                {libelleEncadrant(e)}
              </option>
            ))}
          </select>
        </label>

        <label>
          <span className="etiquette">Lieu</span>
          <select
            className="champ w-44"
            value={filtres.lieuId}
            onChange={(e) => poserFiltre('lieuId', e.target.value)}
          >
            <option value="">Tous</option>
            {lieuxProposes.map(([id, code]) => (
              <option key={id} value={id}>
                {code}
              </option>
            ))}
          </select>
        </label>

        <label>
          <span className="etiquette">Jour</span>
          <select
            className="champ w-44"
            value={filtres.jour}
            onChange={(e) => poserFiltre('jour', e.target.value)}
          >
            <option value="">Tous</option>
            {joursProposes.map((jour) => (
              <option key={jour} value={jour}>
                {libelleJour(jour)}
              </option>
            ))}
          </select>
        </label>

        <label className="flex items-center gap-2 pb-2 text-sm">
          <input
            type="checkbox"
            checked={filtres.inclureInactifs}
            onChange={(e) => setFiltres({ ...filtres, inclureInactifs: e.target.checked })}
          />
          Inclure les inactifs
        </label>

        {(filtres.activiteId ||
          filtres.personnelId ||
          filtres.lieuId ||
          filtres.jour ||
          filtres.inclureInactifs) && (
          <button
            type="button"
            className="bouton-secondaire"
            onClick={() =>
              setFiltres({
                activiteId: '',
                personnelId: '',
                lieuId: '',
                jour: '',
                inclureInactifs: false,
              })
            }
          >
            Effacer
          </button>
        )}

        <Link to="/planning" className="bouton-secondaire ml-auto">
          Voir la grille
        </Link>
      </form>

      <p className="mb-3 text-sm text-encre-500">
        <span className="tabulaire font-medium text-encre-800">{liste.length}</span> créneau(x)
      </p>

      {creneaux.erreur && <Erreur message={creneaux.erreur} onReessayer={creneaux.recharger} />}
      {creneaux.chargement ? (
        <Chargement />
      ) : liste.length === 0 && !filtres.activiteId && !filtres.personnelId ? (
        <div className="carte p-6 text-sm text-encre-500">
          Aucun créneau sur {annee.annee.libelle}.{' '}
          {peut('creneau.creer') && peutEcrire ? (
            <>
              Créez-les un par un, ou{' '}
              <button type="button" className="underline" onClick={() => setDuplication(true)}>
                reprenez la grille d’une année précédente
              </button>
              .
            </>
          ) : (
            'Un gestionnaire doit les créer.'
          )}
        </div>
      ) : (
        <div className="carte">
          <Tableau
            colonnes={colonnes}
            lignes={liste}
            cleDe={(c) => c.id}
            messageVide="Aucun créneau ne correspond aux filtres."
            // Le lien « Modifier » a disparu : le clic sur la ligne le
            // remplace, et doit donc respecter la meme condition — sur une
            // annee cloturee, ouvrir le formulaire menerait a un refus.
            onLigneCliquee={
              peut('creneau.modifier') && peutEcrire
                ? (c) => setEdition({ ouverte: true, creneau: c })
                : undefined
            }
          />
        </div>
      )}

      <p className="mt-4 text-xs text-encre-400">
        Un créneau portant des inscriptions ne peut pas être supprimé : réaffectez-les d’abord.
      </p>

      <CommunicationGroupee
        ouverte={ecrire !== null}
        onFermer={() => setEcrire(null)}
        source={ecrire ? { creneauId: ecrire.id } : null}
        origine={
          ecrire
            ? `${ecrire.activite_libelle} · ${libelleJour(ecrire.jour)} ${formaterHeure(ecrire.heure_debut)} — les élèves inscrits et leurs contacts.`
            : ''
        }
      />

      <FormulaireCreneau
        onEcrire={setEcrire}
        ouverte={edition.ouverte}
        anneeId={annee.anneeId}
        anneeLibelle={annee.annee.libelle}
        creneau={edition.creneau}
        activites={activites.donnees ?? []}
        personnel={personnel.donnees ?? []}
        lieux={lieux.donnees ?? []}
        onFermer={() => setEdition({ ouverte: false, creneau: null })}
        onEnregistre={() => {
          setEdition({ ouverte: false, creneau: null });
          creneaux.recharger();
        }}
        onChangerStatut={
          peutEcrire
            ? (creneau, actif) => {
                setEdition({ ouverte: false, creneau: null });
                void changerStatut(creneau, actif);
              }
            : undefined
        }
      />

      <FormulaireDuplication
        ouverte={duplication}
        anneeCibleId={annee.anneeId}
        anneeCibleLibelle={annee.annee.libelle}
        annees={annee.annees}
        onFermer={() => setDuplication(false)}
        onEnregistre={(bilan) => {
          setDuplication(false);
          creneaux.recharger();
          window.alert(
            `${bilan.crees} créneau(x) repris, ${bilan.ignores} ignoré(s) — déjà présents ou activité désactivée.`,
          );
        }}
      />
    </>
  );
}
